import { css, styled } from 'css-literal-loader/styled';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import defaultProps from 'recompose/defaultProps';
import withProps from 'recompose/withProps';
import Layout from '@4c/layout';

import { shippingDelayed } from '../../config.json';
import withReservation from '../utils/Reservation';
import Card from './Card';
import FormattedPrice from './FormattedPrice';
import { BilledAnnually } from './PriceFootnotes';
import Responsive from './Responsive';
import SubscriptionInput from './SubscriptionInput';
import Total from './Total';
import WarrantyInput from './WarrantyInput';
import WarrantyModal from './WarrantyModal';

import responsiveStyles from '../styles/responsive.module.scss';

const messages = defineMessages({
  included: {
    defaultMessage: 'Included',
    id: 'checkout.warranty.includedPrice',
  },
  quantity: {
    id: 'cartCard.quantity',
    defaultMessage: `{isSmall, select,
      true {Qty {quantity, number}}
      other {{quantity, number}}
    }`,
  },
  deliveryLong: {
    id: 'cartCard.device.shippingLong',
    defaultMessage: 'Delivered within 4 weeks',
  },
  delivery: {
    id: 'cartCard.device.shipping',
    defaultMessage: 'Delivered within 3-7 business days',
  },
});

const emptySpace = <span>&nbsp;</span>;

const Row = defaultProps({ wrap: true, align: 'center' })(
  styled(Layout, null, "Row", require("./CartCard-Row.module.scss"), "row", "row"),
);

const Cell = defaultProps({ display: 'block' })(
  styled(Layout, null, "Cell", require("./CartCard-Cell.module.scss"), "cell", "cell"),
);

const Strong = styled('strong', null, "Strong", require("./CartCard-Strong.module.scss"), "strong", "strong");

const HeaderCell = styled(Cell, null, "HeaderCell", require("./CartCard-HeaderCell.module.scss"), "header-cell", "headerCell");

const SubGroup = withProps({ direction: 'column', pad: 3, justify: 'center' })(
  styled(Layout, null, "SubGroup", require("./CartCard-SubGroup.module.scss"), "sub-group", "subGroup"),
);

const styles = require('./CartCard-styles.module.scss');

const propTypes = {};

class CartCard extends React.Component {
  static propTypes = {
    editable: PropTypes.bool,
  };

  static defaultProps = {
    editable: true,
  };

  state = {
    showWarrantyInfo: false,
  };

  handleWarrantyClose = () => {
    this.setState({ showWarrantyInfo: false });
  };

  handleWarrantyOpen = () => {
    this.setState({ showWarrantyInfo: true });
  };

  handleChangeWarranty = years => {
    this.props.cart.warrantyYears = years;
  };

  handleChangeSubscription = years => {
    this.props.cart.cloudSubscriptionYears = years;
  };

  renderDevicesRow(isSmall) {
    const { cart } = this.props;

    const { numDevicesLightning, numDevicesUsbC } = cart;

    const lightningTitle = (
      <Strong>
        <FormattedMessage
          defaultMessage="Butterfly iQ (Lightning)"
          id="cartCard.device.lightning.heading"
        />
      </Strong>
    );
    const usbcTitle = (
      <Strong>
        <FormattedMessage
          defaultMessage="Butterfly iQ (USB-C)"
          id="cartCard.device.usbc.heading"
        />
      </Strong>
    );
    const lightningQuantity = (
      <FormattedMessage
        {...messages.quantity}
        values={{ quantity: numDevicesLightning, isSmall }}
      />
    );
    const usbcQuantity = (
      <FormattedMessage
        {...messages.quantity}
        values={{ quantity: numDevicesUsbC, isSmall }}
      />
    );
    const delivery = shippingDelayed ? (
      <FormattedMessage tagName="div" {...messages.deliveryLong} />
    ) : (
      <FormattedMessage tagName="div" {...messages.delivery} />
    );

    if (isSmall) {
      return (
        <>
          {!!numDevicesLightning && (
            <>
              <Row noBorder justify="space-between">
                <Cell title>{lightningTitle}</Cell>
                <Cell>{lightningQuantity}</Cell>
                <Cell>
                  <FormattedPrice value={cart.devicesLightningPrice} />
                </Cell>
              </Row>
              <Row noBorder marginTopSmall>
                <Cell>{delivery}</Cell>
              </Row>
            </>
          )}
          {!!numDevicesUsbC && (
            <>
              <Row noBorder={!numDevicesLightning} justify="space-between">
                <Cell title>{usbcTitle}</Cell>
                <Cell>{usbcQuantity}</Cell>
                <Cell>
                  <FormattedPrice value={cart.devicesUsbCPrice} />
                </Cell>
              </Row>
              <Row noBorder marginTopSmall>
                <Cell>{delivery}</Cell>
              </Row>
            </>
          )}
        </>
      );
    }
    return (
      <>
        {!!numDevicesLightning && (
          <Row noBorder>
            <Cell title>
              {lightningTitle}

              {delivery}
            </Cell>
            <Cell>
              <FormattedPrice value={cart.devicePrice} />
            </Cell>
            <Cell>{lightningQuantity}</Cell>
            <Cell>
              <FormattedPrice value={cart.devicesLightningPrice} />
            </Cell>
          </Row>
        )}
        {!!numDevicesUsbC && (
          <Row noBorder={!numDevicesLightning}>
            <Cell title>
              {usbcTitle}

              {delivery}
            </Cell>
            <Cell>
              <FormattedPrice value={cart.devicePrice} />
            </Cell>
            <Cell>{usbcQuantity}</Cell>
            <Cell>
              <FormattedPrice value={cart.devicesUsbCPrice} />
            </Cell>
          </Row>
        )}
      </>
    );
  }

  renderWarrantyRow(isSmall) {
    const { cart, editable } = this.props;
    const { numDevicesLightning, numDevicesUsbC, warrantyYears } = cart;
    const title = (
      <Strong>
        <FormattedMessage
          defaultMessage="Device Warranty"
          id="cartCard.warranty.title"
        />
      </Strong>
    );
    const warrantyInfo = (
      <button
        type="button"
        className={styles.warrantyLink}
        onClick={this.handleWarrantyOpen}
      >
        <FormattedMessage
          defaultMessage="Warranty Info"
          id="cart.warrantyInfo"
        />
      </button>
    );
    const included = (
      <FormattedMessage
        defaultMessage="Included"
        id="cartCard.warranty.includedPrice"
      />
    );
    const quantity = (
      <FormattedMessage
        {...messages.quantity}
        values={{ quantity: numDevicesLightning + numDevicesUsbC, isSmall }}
      />
    );

    if (isSmall) {
      return (
        <Row justify="space-between" wrap>
          <Cell title display="flex">
            <Layout grow align="center" justify="space-between">
              {title}
              {warrantyInfo}
            </Layout>
          </Cell>
          <Cell>{quantity}</Cell>
          <Cell display="flex" direction="column">
            {cart.extendedWarrantiesPrice ? (
              <FormattedPrice value={cart.extendedWarrantiesPrice} />
            ) : (
              included
            )}
          </Cell>
          <Cell main>
            <WarrantyInput
              editable={editable}
              value={warrantyYears}
              id="warranty-input"
              onChange={this.handleChangeWarranty}
            />
          </Cell>
        </Row>
      );
    }

    return (
      <Row>
        <Cell main display="flex" direction="column" pad>
          {editable && (
            <Layout align="center" wrap>
              <span style={{ marginRight: 10 }}>{title}</span>
              {warrantyInfo}
            </Layout>
          )}
          <WarrantyInput
            editable={editable}
            value={warrantyYears}
            id="warranty-input"
            onChange={this.handleChangeWarranty}
          />
        </Cell>
        <Cell display="flex" direction="column">
          {cart.extendedWarrantiesPrice ? (
            <FormattedPrice value={cart.warrantyPrice} />
          ) : (
            included
          )}
        </Cell>
        <Cell>{quantity}</Cell>
        <Cell>
          {cart.extendedWarrantiesPrice ? (
            <FormattedPrice value={cart.extendedWarrantiesPrice} />
          ) : (
            included
          )}
        </Cell>
      </Row>
    );
  }

  renderTeamUsersRow(isSmall) {
    const { cart, editable } = this.props;
    const {
      minCloudUsers,
      numCloudUsers,
      additionalNumCloudUsers,
      cloudSubscriptionYears,
      cloudTeamBasePricePerYear,
      cloudTeamAdditionalPricePerYear,
    } = cart;

    const title = (
      <>
        <Strong>
          <FormattedMessage
            defaultMessage="Team Subscription:"
            id="cartCard.teamUsers.heading"
          />
        </Strong>{' '}
        <FormattedMessage
          defaultMessage="{numCloudUsers} users"
          id="cartCard.teamUsers.subheading"
          values={{ numCloudUsers }}
        />
      </>
    );

    const baseUsers = (
      <FormattedMessage
        id="cartCard.numBaseTeamUsers"
        defaultMessage="{minCloudUsers} Users included"
        values={{ minCloudUsers }}
      />
    );

    if (isSmall) {
      return (
        <Row justify="space-between" wrap>
          <Cell title>{title}</Cell>
          <Cell display="flex" main direction="column" pad="2">
            <Layout justify="space-between">
              <div>{baseUsers}</div>
              <FormattedPrice value={cart.baseCloudUsersPrice} perYear />
            </Layout>
            <Layout justify="space-between">
              <div>
                <FormattedMessage
                  id="cartCard.numAdditionalTeamUsers"
                  defaultMessage="{users} Additional Users"
                  values={{ users: cart.additionalNumCloudUsers || 0 }}
                />
              </div>
              <FormattedPrice value={cart.additionalCloudUsersPrice} perYear />
            </Layout>
          </Cell>

          <Cell display="flex" main justify="space-between">
            <SubscriptionInput
              id="subscription-input"
              editable={editable}
              onChange={this.handleChangeSubscription}
              value={cloudSubscriptionYears || 1}
            />
            <BilledAnnually parens />
          </Cell>
        </Row>
      );
    }

    return (
      <Row align="stretch">
        <Cell title>
          <div>{title}</div>
          <SubGroup border>
            <div>{baseUsers}</div>
            <div>
              <FormattedMessage
                id="cartCard.additionalTeamUsers"
                defaultMessage="Additional Users"
              />
            </div>
          </SubGroup>
          <SubscriptionInput
            editable={editable}
            id="subscription-input"
            onChange={this.handleChangeSubscription}
            value={cloudSubscriptionYears || 1}
          />
        </Cell>
        <Cell display="flex" direction="column">
          {emptySpace}
          <SubGroup>
            <div>
              <FormattedPrice value={cloudTeamBasePricePerYear} perYear />
            </div>
            <div>
              <FormattedPrice
                value={cloudTeamAdditionalPricePerYear}
                perYear
              />
            </div>
          </SubGroup>
          <BilledAnnually parens />
        </Cell>
        <Cell>
          {emptySpace}
          <SubGroup>
            <div>{1}</div>
            <div>{additionalNumCloudUsers || 0}</div>
          </SubGroup>
        </Cell>
        <Cell>
          {emptySpace}
          <SubGroup>
            <div>
              <FormattedPrice value={cart.baseCloudUsersPrice} />
            </div>
            <div>
              <FormattedPrice value={cart.additionalCloudUsersPrice} />
            </div>
          </SubGroup>
        </Cell>
      </Row>
    );
  }

  renderUsersRow(isSmall) {
    const { cart, editable } = this.props;
    const { numCloudUsers, cloudSubscriptionYears } = cart;

    const title = (
      <Strong>
        <FormattedMessage
          defaultMessage="Individual Subscription"
          id="cartCard.individualUsers.heading"
        />
      </Strong>
    );
    const quantity = (
      <FormattedMessage
        {...messages.quantity}
        values={{ quantity: numCloudUsers, isSmall }}
      />
    );

    if (isSmall) {
      return (
        <Row justify="space-between" wrap>
          <Cell title>
            <div>{title}</div>
          </Cell>
          <Cell>{quantity}</Cell>
          <Cell>
            <FormattedPrice value={cart.cloudUsersPrice} />
          </Cell>
          <Cell display="flex" main justify="space-between">
            <SubscriptionInput
              editable={editable}
              onChange={this.handleChangeSubscription}
              value={cloudSubscriptionYears}
            />

            <BilledAnnually parens />
          </Cell>
        </Row>
      );
    }

    return (
      <Row>
        <Cell title>
          <div>{title}</div>
          {editable ? (
            <SubscriptionInput
              editable={editable}
              onChange={this.handleChangeSubscription}
              value={cloudSubscriptionYears}
            />
          ) : (
            <FormattedMessage
              id="cartCard.individualTerm"
              defaultMessage="{cloudSubscriptionYears} Year"
              values={{ cloudSubscriptionYears }}
            />
          )}
        </Cell>
        <Cell display="flex" direction="column">
          <FormattedPrice value={cart.cloudUserPricePerYear} perYear />
          <BilledAnnually parens />
        </Cell>
        <Cell>{quantity}</Cell>
        {!isSmall && (
          <Cell>
            <FormattedPrice value={cart.cloudUsersPrice} />
          </Cell>
        )}
      </Row>
    );
  }

  renderTotal(isSmall, title, total) {
    return (
      <Layout
        align="flex-end"
        className={styles.totals}
        justify={isSmall ? 'space-between' : 'flex-end'}
      >
        <span>{title}</span>
        <span>
          <FormattedPrice value={total} />
        </span>
      </Layout>
    );
  }

  render() {
    const { cart } = this.props;

    return (
      <Card>
        <WarrantyModal
          show={this.state.showWarrantyInfo}
          onHide={this.handleWarrantyClose}
        />
        <Responsive smDown>
          {isSmall => (
            <Card.Body display="flex">
              {!isSmall && (
                <Row className={responsiveStyles.noPrint}>
                  <HeaderCell>
                    <FormattedMessage
                      defaultMessage="Product"
                      id="cartCard.product"
                    />
                  </HeaderCell>
                  <HeaderCell>
                    <FormattedMessage
                      defaultMessage="price"
                      id="cartCard.price"
                    />
                  </HeaderCell>
                  <HeaderCell>
                    <FormattedMessage
                      defaultMessage="quantity"
                      id="cartCard.quantity"
                    />
                  </HeaderCell>
                  <HeaderCell>
                    <FormattedMessage
                      defaultMessage="total"
                      id="cartCard.total"
                    />
                  </HeaderCell>
                </Row>
              )}
              {this.renderDevicesRow(isSmall)}

              {this.renderWarrantyRow(isSmall)}

              {/* eslint-disable-next-line no-nested-ternary */}
              {cart.remote.isDeviceOnly
                ? null
                : cart.plan === 'TEAM'
                ? this.renderTeamUsersRow(isSmall)
                : this.renderUsersRow(isSmall)}

              <Row justify="flex-end">
                <Layout
                  pad={3}
                  direction="column"
                  flex={isSmall ? '1' : '0 1 50%'}
                >
                  {cart.remote.payment && (
                    <>
                      {this.renderTotal(
                        isSmall,
                        <FormattedMessage
                          defaultMessage="Subtotal"
                          id="cartCard.subtotal"
                        />,
                        cart.subtotal,
                      )}
                      {this.renderTotal(
                        isSmall,
                        <FormattedMessage
                          defaultMessage="Tax"
                          id="cartCard.taxes"
                        />,
                        cart.taxPrice,
                      )}
                      {this.renderTotal(
                        isSmall,
                        <FormattedMessage
                          defaultMessage="Shipping"
                          id="cartCard.shipping"
                        />,
                        cart.shippingPrice,
                      )}
                    </>
                  )}
                  <Layout
                    align="flex-end"
                    className={styles.totals}
                    justify={isSmall ? 'space-between' : 'flex-end'}
                  >
                    <span className={styles.subtotal}>
                      {cart.remote.payment ? (
                        <FormattedMessage
                          defaultMessage="Total"
                          id="cartCard.cartTotal"
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="Subtotal"
                          id="cartCard.notPaid.subtotal"
                        />
                      )}
                    </span>
                    <Total value={cart.total} />
                  </Layout>
                </Layout>
              </Row>
            </Card.Body>
          )}
        </Responsive>
      </Card>
    );
  }
}
CartCard.propTypes = propTypes;

export default withReservation(CartCard);
